import { useEffect,useMemo,useState } from "react";
import {
  Box,Typography,Button,TextField,CircularProgress,Table,TableBody,TableCell,
  TableContainer,TableHead,TableRow,Dialog,DialogTitle,DialogContent,
  DialogActions,FormControl,InputLabel,Select,MenuItem,IconButton
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useAppTheme } from "../../context/ThemeContext";
import { useAlert } from "../../context/AlertContext";
import PhotoCropper from "./AdminPlayers/PhotoCropper";
import Carnets from "./AdminPlayers/Carnets";

const getCookie=name=>{
  const value=document.cookie.split("; ").find(row=>row.startsWith(`${name}=`));
  return value?decodeURIComponent(value.split("=")[1]):"";
};

const readResponse=async response=>{
  const contentType=response.headers.get("content-type")||"";
  if(contentType.includes("application/json")) return response.json();
  if(!response.ok) throw new Error("El servidor no respondió correctamente.");
  return {};
};

const formatDate=value=>{
  if(!value) return "—";
  const date=new Date(value);
  if(Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("es-EC",{day:"2-digit",month:"2-digit",year:"numeric",hour:"2-digit",minute:"2-digit"});
};

const emptyForm=()=>({
  name:"",
  identification:"",
  birth_date:"",
  photo:null,
  identification_photo:null,
  categories:[]
});

export default function AdminPlayers(){
  const {colors,typography,shape}=useAppTheme();
  const {showSuccess,showError}=useAlert();

  const [loading,setLoading]=useState(true);
  const [saving,setSaving]=useState(false);
  const [requesting,setRequesting]=useState(false);
  const [players,setPlayers]=useState([]);
  const [categories,setCategories]=useState([]);
  const [team,setTeam]=useState(null);
  const [registrationOpen,setRegistrationOpen]=useState(false);
  const [registrationWindow,setRegistrationWindow]=useState(null);
  const [search,setSearch]=useState("");
  const [categoryFilter,setCategoryFilter]=useState("");

  const [formOpen,setFormOpen]=useState(false);
  const [selectedPlayer,setSelectedPlayer]=useState(null);
  const [form,setForm]=useState(emptyForm());
  const [detailPermissions,setDetailPermissions]=useState({update:false,categories:false});

  const [cropOpen,setCropOpen]=useState(false);
  const [cropSource,setCropSource]=useState(null);
  const [cropType,setCropType]=useState("player");
  const [photoPreview,setPhotoPreview]=useState("");
  const [documentPreview,setDocumentPreview]=useState("");

  const [carnetOpen,setCarnetOpen]=useState(false);
  const [carnetPlayer,setCarnetPlayer]=useState(null);

  const [requestOpen,setRequestOpen]=useState(false);
  const [requestPlayer,setRequestPlayer]=useState(null);
  const [requestType,setRequestType]=useState("UPDATE");
  const [requestReason,setRequestReason]=useState("");

  const selectedCategory=useMemo(
    ()=>categories.find(item=>item.slug===categoryFilter)||null,
    [categories,categoryFilter]
  );

  useEffect(()=>{
    return ()=>{
      if(photoPreview&&photoPreview.startsWith("blob:")) URL.revokeObjectURL(photoPreview);
      if(documentPreview&&documentPreview.startsWith("blob:")) URL.revokeObjectURL(documentPreview);
    };
  },[photoPreview,documentPreview]);

  const loadOptions=async()=>{
    const response=await fetch("/competition/delegate/players/options/",{credentials:"include"});
    const data=await readResponse(response);

    if(!response.ok){
      throw new Error(data.error||"No se pudo cargar la información del equipo.");
    }

    const availableCategories=Array.isArray(data.categories)?data.categories:[];

    setTeam(data.team||null);
    setCategories(availableCategories);
    setRegistrationOpen(Boolean(data.registration_open));
    setRegistrationWindow(data.registration_window||null);

    return availableCategories;
  };

  const loadPlayers=async(categorySlug=categoryFilter)=>{
    if(!categorySlug){
      setPlayers([]);
      return;
    }

    const params=new URLSearchParams();
    params.set("category",categorySlug);

    if(search.trim()){
      params.set("search",search.trim());
    }

    const response=await fetch(
      `/competition/delegate/players/?${params.toString()}`,
      {credentials:"include"}
    );

    const data=await readResponse(response);

    if(!response.ok){
      throw new Error(data.error||"No se pudieron cargar los jugadores.");
    }

    setPlayers(Array.isArray(data.results)?data.results:[]);
    setRegistrationOpen(Boolean(data.registration_open));
    setRegistrationWindow(data.registration_window||null);
  };

  const loadInitial=async()=>{
    try{
      setLoading(true);

      const availableCategories=await loadOptions();
      const initialCategory=availableCategories[0]?.slug||"";

      setCategoryFilter(initialCategory);

      if(initialCategory){
        await loadPlayers(initialCategory);
      }else{
        setPlayers([]);
      }
    }catch(error){
      showError(error.message||"No se pudo cargar la información.");
    }finally{
      setLoading(false);
    }
  };

  useEffect(()=>{
    loadInitial();
  },[]);

  useEffect(()=>{
    if(loading||!categoryFilter) return;

    const timer=setTimeout(()=>{
      loadPlayers(categoryFilter).catch(error=>{
        showError(error.message||"No se pudieron cargar los jugadores.");
      });
    },300);

    return ()=>clearTimeout(timer);
  },[search,categoryFilter]);

  const clearPreview=(preview,setPreview)=>{
    if(preview&&preview.startsWith("blob:")){
      URL.revokeObjectURL(preview);
    }

    setPreview("");
  };

  const resetImages=()=>{
    clearPreview(photoPreview,setPhotoPreview);
    clearPreview(documentPreview,setDocumentPreview);
    setCropSource(null);
    setCropType("player");
    setCropOpen(false);
  };

  const openCreate=()=>{
    if(!registrationOpen){
      showError("El mercado de fichajes está cerrado.");
      return;
    }

    if(!categoryFilter){
      showError("Selecciona una categoría.");
      return;
    }

    resetImages();
    setSelectedPlayer(null);
    setDetailPermissions({update:true,categories:true});

    setForm({
      ...emptyForm(),
      categories:[
        {
          slug:categoryFilter,
          jersey_number:""
        }
      ]
    });

    setFormOpen(true);
  };

  const openEdit=async player=>{
    try{
      const response=await fetch(
        `/competition/delegate/players/${player.slug}/`,
        {credentials:"include"}
      );

      const data=await readResponse(response);

      if(!response.ok){
        throw new Error(data.error||"No se pudo cargar el jugador.");
      }

      resetImages();

      setSelectedPlayer(data);
      setRegistrationOpen(Boolean(data.registration_open));

      setDetailPermissions({
        update:Boolean(data.permissions?.update),
        categories:Boolean(data.permissions?.categories)
      });

      setForm({
        name:data.name||"",
        identification:data.identification||"",
        birth_date:data.birth_date||"",
        photo:null,
        identification_photo:null,
        categories:(data.categories||[]).map(item=>({
          slug:item.category?.slug||"",
          jersey_number:item.jersey_number??""
        }))
      });

      setPhotoPreview(data.photo||"");
      setDocumentPreview(data.identification_photo||"");
      setFormOpen(true);
    }catch(error){
      showError(error.message||"No se pudo cargar el jugador.");
    }
  };

  const closeForm=()=>{
    if(saving) return;

    setFormOpen(false);
    setSelectedPlayer(null);
    setForm(emptyForm());
    setDetailPermissions({update:false,categories:false});
    resetImages();
  };

  const handleFieldChange=event=>{
    const {name,value}=event.target;
    setForm(current=>({...current,[name]:value}));
  };

  const openCrop=(file,type)=>{
    setCropType(type);
    setCropSource(file);
    setCropOpen(true);
  };

  const selectPlayerPhoto=event=>{
    const file=event.target.files?.[0]||null;
    event.target.value="";

    if(!file) return;

    if(!file.type.startsWith("image/")){
      showError("Selecciona una imagen válida.");
      return;
    }

    openCrop(file,"player");
  };

  const selectDocument=event=>{
    const file=event.target.files?.[0]||null;
    event.target.value="";

    if(!file) return;

    if(!file.type.startsWith("image/")){
      showError("Selecciona una imagen válida para el documento.");
      return;
    }

    openCrop(file,"document");
  };

  const confirmCrop=file=>{
    const preview=URL.createObjectURL(file);

    if(cropType==="document"){
      clearPreview(documentPreview,setDocumentPreview);

      setForm(current=>({
        ...current,
        identification_photo:file
      }));

      setDocumentPreview(preview);
    }else{
      clearPreview(photoPreview,setPhotoPreview);

      setForm(current=>({
        ...current,
        photo:file
      }));

      setPhotoPreview(preview);
    }

    setCropOpen(false);
    setCropSource(null);
  };

  const addCategory=()=>{
    const used=new Set(form.categories.map(item=>item.slug));
    const available=categories.find(item=>!used.has(item.slug));

    if(!available){
      showError("No existen más categorías disponibles para agregar.");
      return;
    }

    const defaultJersey=form.categories.find(item=>item.jersey_number!=="")?.jersey_number??"";

    setForm(current=>({
      ...current,
      categories:[
        ...current.categories,
        {
          slug:available.slug,
          jersey_number:defaultJersey
        }
      ]
    }));
  };

  const updateCategory=(index,field,value)=>{
    setForm(current=>({
      ...current,
      categories:current.categories.map((item,itemIndex)=>
        itemIndex===index
          ?{...item,[field]:value}
          :item
      )
    }));
  };

  const removeCategory=index=>{
    if(form.categories.length===1){
      showError("El jugador debe permanecer inscrito al menos en una categoría.");
      return;
    }

    setForm(current=>({
      ...current,
      categories:current.categories.filter((_,itemIndex)=>itemIndex!==index)
    }));
  };

  const validateForm=()=>{
    if(!form.name.trim()){
      showError("Ingresa el nombre completo del jugador.");
      return false;
    }

    if(!form.identification.trim()){
      showError("Ingresa la identificación del jugador.");
      return false;
    }

    if(!form.birth_date){
      showError("Selecciona la fecha de nacimiento.");
      return false;
    }

    if(!selectedPlayer&&!form.photo){
      showError("Selecciona y recorta la fotografía del jugador.");
      return false;
    }

    if(!selectedPlayer&&!form.identification_photo){
      showError("Selecciona y recorta la fotografía del documento de identidad.");
      return false;
    }

    if(!form.categories.length){
      showError("Selecciona al menos una categoría.");
      return false;
    }

    const used=new Set();

    for(const item of form.categories){
      if(!item.slug){
        showError("Selecciona una categoría válida.");
        return false;
      }

      if(used.has(item.slug)){
        showError("No puedes seleccionar dos veces la misma categoría.");
        return false;
      }

      used.add(item.slug);

      const jersey=Number(item.jersey_number);

      if(item.jersey_number===""||!Number.isInteger(jersey)||jersey<=0){
        showError("Todos los jugadores deben tener un dorsal válido.");
        return false;
      }
    }

    return true;
  };

  const handleSave=async()=>{
    if(!validateForm()) return;

    try{
      setSaving(true);

      const body=new FormData();

      body.append("name",form.name.trim());
      body.append("identification",form.identification.trim());
      body.append("birth_date",form.birth_date);

      body.append(
        "categories",
        JSON.stringify(
          form.categories.map(item=>({
            slug:item.slug,
            jersey_number:Number(item.jersey_number)
          }))
        )
      );

      if(form.photo){
        body.append("photo",form.photo);
      }

      if(form.identification_photo){
        body.append("identification_photo",form.identification_photo);
      }

      const editing=Boolean(selectedPlayer);

      const response=await fetch(
        editing
          ?`/competition/delegate/players/${selectedPlayer.slug}/`
          :"/competition/delegate/players/",
        {
          method:editing?"PATCH":"POST",
          credentials:"include",
          headers:{
            "X-CSRFToken":getCookie("csrftoken")
          },
          body
        }
      );

      const data=await readResponse(response);

      if(!response.ok){
        if(response.status===403&&data.requires_request&&editing){
          setFormOpen(false);

          openRequest(
            selectedPlayer,
            data.request_type||"UPDATE",
            data.error||"Debes solicitar autorización."
          );

          return;
        }

        throw new Error(
          data.error||
          (editing
            ?"No se pudo actualizar el jugador."
            :"No se pudo registrar el jugador.")
        );
      }

      closeForm();

      showSuccess(
        editing
          ?"Jugador actualizado correctamente."
          :"Jugador registrado correctamente."
      );

      await loadPlayers(categoryFilter);
    }catch(error){
      showError(error.message||"No se pudo guardar el jugador.");
    }finally{
      setSaving(false);
    }
  };

  const openRequest=(player,type,message="")=>{
    setRequestPlayer(player);
    setRequestType(type);
    setRequestReason("");
    setRequestOpen(true);

    if(message){
      showError(message);
    }
  };

  const closeRequest=()=>{
    if(requesting) return;

    setRequestOpen(false);
    setRequestPlayer(null);
    setRequestType("UPDATE");
    setRequestReason("");
  };

  const handleRequest=async()=>{
    if(!requestPlayer||!requestReason.trim()){
      showError("Ingresa el motivo de la solicitud.");
      return;
    }

    try{
      setRequesting(true);

      const response=await fetch(
        "/competition/delegate/change-requests/",
        {
          method:"POST",
          credentials:"include",
          headers:{
            "Content-Type":"application/json",
            "X-CSRFToken":getCookie("csrftoken")
          },
          body:JSON.stringify({
            player_slug:requestPlayer.slug,
            request_type:requestType,
            reason:requestReason.trim()
          })
        }
      );

      const data=await readResponse(response);

      if(!response.ok){
        throw new Error(data.error||"No se pudo enviar la solicitud.");
      }

      closeRequest();
      showSuccess("Solicitud enviada correctamente.");
    }catch(error){
      showError(error.message||"No se pudo enviar la solicitud.");
    }finally{
      setRequesting(false);
    }
  };

  const openCarnet=player=>{
    setCarnetPlayer(player);
    setCarnetOpen(true);
  };

  const filteredCategoryOptions=index=>{
    const selected=new Set(
      form.categories
        .filter((_,itemIndex)=>itemIndex!==index)
        .map(item=>item.slug)
    );

    return categories.filter(item=>!selected.has(item.slug));
  };

  const getPlayerJersey=player=>{
    if(!categoryFilter) return "—";

    const registration=(player.categories||[]).find(
      item=>item.category?.slug===categoryFilter
    );

    return registration?.jersey_number??"—";
  };

  const marketText=useMemo(()=>{
    if(registrationOpen&&registrationWindow){
      return `Abierto del ${formatDate(registrationWindow.starts_at)} al ${formatDate(registrationWindow.ends_at)}. Durante este período puedes registrar y actualizar jugadores.`;
    }

    return "El mercado de fichajes se encuentra cerrado. Para modificar jugadores debes solicitar autorización.";
  },[registrationOpen,registrationWindow]);

  const headCellSx={
    py:1.2,
    px:1.5,
    backgroundColor:colors.tableHeader,
    color:colors.tableHeaderText,
    fontSize:typography.sizes.sm,
    fontWeight:700,
    borderColor:colors.border,
    whiteSpace:"nowrap"
  };

  const bodyCellSx={
    py:1.2,
    px:1.5,
    color:colors.text,
    fontSize:typography.sizes.sm,
    borderColor:colors.border
  };

  if(loading){
    return (
      <Box sx={{minHeight:300,display:"flex",alignItems:"center",justifyContent:"center"}}>
        <CircularProgress size={25} sx={{color:colors.secondary}} />
      </Box>
    );
  }

  return (
    <Box sx={{width:"100%",maxWidth:1300,mx:"auto"}}>
      <Box sx={{display:"flex",flexDirection:{xs:"column",md:"row"},justifyContent:"space-between",alignItems:{xs:"stretch",md:"flex-start"},gap:2,mb:2}}>
        <Box>
          <Typography sx={{fontSize:{xs:typography.sizes.xl,md:typography.sizes.pageTitle},fontWeight:700,color:colors.text}}>
            Jugadores
          </Typography>

          <Typography sx={{mt:0.3,fontSize:typography.sizes.md,color:colors.textSecondary}}>
            {team?.name||"Equipo"}
            {selectedCategory?` · ${selectedCategory.name}`:""}
          </Typography>
        </Box>

        {registrationOpen&&categoryFilter&&(
          <Button
            size="small"
            variant="contained"
            onClick={openCreate}
            sx={{
              px:2,
              alignSelf:{xs:"flex-start",md:"center"},
              backgroundColor:colors.secondary,
              color:colors.buttonPrimaryText,
              boxShadow:"none",
              textTransform:"none",
              "&:hover":{
                backgroundColor:colors.secondaryHover,
                boxShadow:"none"
              }
            }}
          >
            Nuevo jugador
          </Button>
        )}
      </Box>

      <Box sx={{mb:2,p:1.5,border:`1px solid ${colors.border}`,borderRadius:shape.radius,backgroundColor:colors.surface}}>
        <Typography sx={{fontSize:typography.sizes.md,fontWeight:700,color:registrationOpen?colors.success:colors.text}}>
          {registrationOpen?"Mercado de fichajes abierto":"Mercado de fichajes cerrado"}
        </Typography>

        <Typography sx={{mt:0.3,fontSize:typography.sizes.sm,color:colors.textSecondary,lineHeight:1.5}}>
          {marketText}
        </Typography>
      </Box>

      <Box
        sx={{
          mb:1.5,
          p:1.5,
          display:"grid",
          gridTemplateColumns:{
            xs:"1fr",
            md:"260px minmax(240px,1fr)"
          },
          gap:1.2,
          border:`1px solid ${colors.border}`,
          borderRadius:shape.radius,
          backgroundColor:colors.surface
        }}
      >
        <FormControl size="small" fullWidth>
          <InputLabel>Categoría</InputLabel>

          <Select
            value={categoryFilter}
            label="Categoría"
            onChange={event=>{
              setCategoryFilter(event.target.value);
              setSearch("");
            }}
            MenuProps={{disableScrollLock:true}}
          >
            {categories.map(category=>(
              <MenuItem
                key={category.slug}
                value={category.slug}
              >
                {category.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          size="small"
          value={search}
          onChange={event=>setSearch(event.target.value)}
          placeholder={`Buscar jugador${selectedCategory?` en ${selectedCategory.name}`:""}`}
          fullWidth
        />
      </Box>

      {!categories.length?(
        <Box
          sx={{
            py:5,
            px:2,
            textAlign:"center",
            border:`1px solid ${colors.border}`,
            borderRadius:shape.radiusLarge,
            backgroundColor:colors.surface
          }}
        >
          <Typography sx={{fontSize:typography.sizes.md,color:colors.textSecondary}}>
            El equipo no tiene categorías asignadas.
          </Typography>
        </Box>
      ):(
        <TableContainer sx={{border:`1px solid ${colors.border}`,borderRadius:shape.radiusLarge,backgroundColor:colors.surface,overflowX:"auto"}}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={headCellSx}>Jugador</TableCell>
                <TableCell sx={headCellSx}>Identificación</TableCell>
                <TableCell sx={headCellSx}>Dorsal</TableCell>
                <TableCell sx={headCellSx}>Estado</TableCell>
                <TableCell sx={{...headCellSx,textAlign:"right"}}>Acciones</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {!players.length?(
                <TableRow>
                  <TableCell colSpan={5} sx={{py:5,textAlign:"center",border:0}}>
                    <Typography sx={{fontSize:typography.sizes.md,color:colors.textSecondary}}>
                      {selectedCategory
                        ?`No existen jugadores registrados en ${selectedCategory.name}.`
                        :"No existen jugadores registrados."}
                    </Typography>
                  </TableCell>
                </TableRow>
              ):players.map(player=>(
                <TableRow
                  key={player.slug}
                  sx={{
                    "&:last-child td":{borderBottom:0},
                    "&:hover":{backgroundColor:colors.backgroundSoft}
                  }}
                >
                  <TableCell sx={bodyCellSx}>
                    <Box sx={{display:"flex",alignItems:"center",gap:1.2}}>
                      {player.photo&&(
                        <Box
                          component="img"
                          src={player.photo}
                          alt={player.name}
                          sx={{
                            width:36,
                            height:48,
                            borderRadius:shape.radiusSmall,
                            objectFit:"cover",
                            border:`1px solid ${colors.border}`
                          }}
                        />
                      )}

                      <Box>
                        <Typography sx={{fontSize:typography.sizes.sm,fontWeight:600,color:colors.text}}>
                          {player.name}
                        </Typography>

                        <Typography sx={{fontSize:typography.sizes.xs,color:colors.textSecondary}}>
                          {player.age} años
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>

                  <TableCell sx={bodyCellSx}>
                    {player.identification}
                  </TableCell>

                  <TableCell sx={bodyCellSx}>
                    <Typography component="span" sx={{fontSize:typography.sizes.sm,fontWeight:600,color:colors.text}}>
                      #{getPlayerJersey(player)}
                    </Typography>
                  </TableCell>

                  <TableCell sx={bodyCellSx}>
                    <Typography
                      component="span"
                      sx={{
                        fontSize:typography.sizes.sm,
                        fontWeight:600,
                        color:player.active?colors.success:colors.textMuted
                      }}
                    >
                      {player.active?"Activo":"Inactivo"}
                    </Typography>
                  </TableCell>

                  <TableCell sx={{...bodyCellSx,textAlign:"right",whiteSpace:"nowrap"}}>
                    <Button
                      size="small"
                      variant="text"
                      onClick={()=>openCarnet(player)}
                      sx={{
                        minWidth:"auto",
                        px:1.2,
                        color:colors.secondary,
                        textTransform:"none",
                        fontWeight:600
                      }}
                    >
                      Carnet
                    </Button>

                    <Button
                      size="small"
                      variant="text"
                      onClick={()=>openEdit(player)}
                      sx={{
                        minWidth:"auto",
                        px:1.2,
                        color:colors.secondary,
                        textTransform:"none",
                        fontWeight:600
                      }}
                    >
                      {registrationOpen?"Editar":"Ver / solicitar"}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog
        open={formOpen}
        onClose={closeForm}
        fullWidth
        maxWidth="md"
        disableScrollLock
        PaperProps={{
          sx:{
            borderRadius:shape.radiusLarge,
            boxShadow:"none",
            border:`1px solid ${colors.border}`,
            backgroundColor:colors.surface
          }
        }}
      >
        <DialogTitle sx={{pb:1,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <Typography sx={{fontSize:typography.sizes.xl,fontWeight:700,color:colors.text}}>
            {selectedPlayer?"Jugador":"Nuevo jugador"}
          </Typography>

          <IconButton size="small" onClick={closeForm} disabled={saving} sx={{color:colors.textSecondary}}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          {selectedPlayer&&!registrationOpen&&(
            <Box sx={{mb:2,px:1.5,py:1.2,border:`1px solid ${colors.border}`,borderRadius:shape.radiusSmall,backgroundColor:colors.backgroundSoft}}>
              <Typography sx={{fontSize:typography.sizes.sm,fontWeight:600,color:colors.text}}>
                Mercado de fichajes cerrado
              </Typography>

              <Typography sx={{mt:0.3,fontSize:typography.sizes.sm,color:colors.textSecondary}}>
                Las modificaciones requieren autorización de la administración.
              </Typography>
            </Box>
          )}

          {!selectedPlayer&&selectedCategory&&(
            <Box
              sx={{
                mb:2,
                px:1.5,
                py:1,
                border:`1px solid ${colors.border}`,
                borderRadius:shape.radiusSmall,
                backgroundColor:colors.backgroundSoft
              }}
            >
              <Typography sx={{fontSize:typography.sizes.sm,color:colors.textSecondary}}>
                Registrando jugador inicialmente en{" "}
                <Typography component="span" sx={{fontSize:"inherit",fontWeight:600,color:colors.text}}>
                  {selectedCategory.name}
                </Typography>
              </Typography>
            </Box>
          )}

          <Box sx={{display:"grid",gridTemplateColumns:{xs:"1fr",md:"1fr 1fr"},gap:1.5}}>
            <TextField
              size="small"
              label="Nombre completo"
              name="name"
              value={form.name}
              onChange={handleFieldChange}
              disabled={Boolean(selectedPlayer)&&!registrationOpen&&!detailPermissions.update}
              fullWidth
            />

            <TextField
              size="small"
              label="Identificación"
              name="identification"
              value={form.identification}
              onChange={handleFieldChange}
              disabled={Boolean(selectedPlayer)&&!registrationOpen&&!detailPermissions.update}
              fullWidth
            />

            <TextField
              size="small"
              type="date"
              label="Fecha de nacimiento"
              name="birth_date"
              value={form.birth_date}
              onChange={handleFieldChange}
              disabled={Boolean(selectedPlayer)&&!registrationOpen&&!detailPermissions.update}
              InputLabelProps={{shrink:true}}
              fullWidth
            />
          </Box>

          <Box sx={{mt:2,display:"grid",gridTemplateColumns:{xs:"1fr",md:"1fr 1fr"},gap:2}}>
            <Box>
              <Typography sx={{mb:0.7,fontSize:typography.sizes.sm,fontWeight:600,color:colors.text}}>
                Foto del jugador
              </Typography>

              <Box
                sx={{
                  mb:1,
                  width:150,
                  height:200,
                  borderRadius:shape.radius,
                  overflow:"hidden",
                  border:`1px solid ${colors.border}`,
                  backgroundColor:colors.backgroundSoft,
                  display:"flex",
                  alignItems:"center",
                  justifyContent:"center"
                }}
              >
                {photoPreview?(
                  <Box
                    component="img"
                    src={photoPreview}
                    alt="Fotografía del jugador"
                    sx={{width:"100%",height:"100%",objectFit:"cover",display:"block"}}
                  />
                ):(
                  <Typography sx={{px:1,textAlign:"center",fontSize:typography.sizes.xs,color:colors.textSecondary}}>
                    Fotografía vertical
                  </Typography>
                )}
              </Box>

              <Button
                component="label"
                size="small"
                variant="outlined"
                disabled={Boolean(selectedPlayer)&&!registrationOpen&&!detailPermissions.update}
                sx={{
                  borderColor:colors.buttonOutlinedBorder,
                  color:colors.buttonOutlinedText,
                  textTransform:"none"
                }}
              >
                {photoPreview?"Seleccionar otra foto":"Seleccionar fotografía"}
                <input hidden type="file" accept="image/*" onChange={selectPlayerPhoto} />
              </Button>

              <Typography sx={{mt:0.7,fontSize:typography.sizes.xs,color:colors.textSecondary}}>
                La fotografía se recorta en formato vertical para el carnet.
              </Typography>
            </Box>

            <Box>
              <Typography sx={{mb:0.7,fontSize:typography.sizes.sm,fontWeight:600,color:colors.text}}>
                Documento de identidad
              </Typography>

              <Box
                sx={{
                  mb:1,
                  width:"100%",
                  maxWidth:320,
                  aspectRatio:"16 / 10",
                  borderRadius:shape.radius,
                  overflow:"hidden",
                  border:`1px solid ${colors.border}`,
                  backgroundColor:colors.backgroundSoft,
                  display:"flex",
                  alignItems:"center",
                  justifyContent:"center"
                }}
              >
                {documentPreview?(
                  <Box
                    component="img"
                    src={documentPreview}
                    alt="Documento de identidad"
                    sx={{width:"100%",height:"100%",objectFit:"cover",display:"block"}}
                  />
                ):(
                  <Typography sx={{px:2,textAlign:"center",fontSize:typography.sizes.xs,color:colors.textSecondary}}>
                    Fotografía horizontal de la cédula
                  </Typography>
                )}
              </Box>

              <Button
                component="label"
                size="small"
                variant="outlined"
                disabled={Boolean(selectedPlayer)&&!registrationOpen&&!detailPermissions.update}
                sx={{
                  borderColor:colors.buttonOutlinedBorder,
                  color:colors.buttonOutlinedText,
                  textTransform:"none"
                }}
              >
                {documentPreview?"Seleccionar otra cédula":"Seleccionar cédula"}
                <input hidden type="file" accept="image/*" onChange={selectDocument} />
              </Button>

              <Typography sx={{mt:0.7,fontSize:typography.sizes.xs,color:colors.textSecondary}}>
                La cédula se recorta horizontalmente. Es privada y no aparece en el carnet.
              </Typography>
            </Box>
          </Box>

          <Box sx={{mt:2.5,pt:2,borderTop:`1px solid ${colors.border}`}}>
            <Box sx={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:1,mb:1.2}}>
              <Box>
                <Typography sx={{fontSize:typography.sizes.md,fontWeight:700,color:colors.text}}>
                  Categorías y dorsales
                </Typography>

                <Typography sx={{mt:0.2,fontSize:typography.sizes.xs,color:colors.textSecondary}}>
                  El jugador puede pertenecer a varias categorías del mismo equipo. El dorsal habitual se reutiliza automáticamente.
                </Typography>
              </Box>

              <Button
                size="small"
                variant="outlined"
                onClick={addCategory}
                disabled={Boolean(selectedPlayer)&&!registrationOpen&&!detailPermissions.categories}
                sx={{
                  borderColor:colors.buttonOutlinedBorder,
                  color:colors.buttonOutlinedText,
                  textTransform:"none"
                }}
              >
                Agregar categoría
              </Button>
            </Box>

            {!form.categories.length?(
              <Box sx={{py:2,px:1.5,border:`1px solid ${colors.border}`,borderRadius:shape.radiusSmall,backgroundColor:colors.backgroundSoft}}>
                <Typography sx={{fontSize:typography.sizes.sm,color:colors.textSecondary}}>
                  No se han seleccionado categorías.
                </Typography>
              </Box>
            ):(
              <Box sx={{display:"flex",flexDirection:"column",gap:1}}>
                {form.categories.map((item,index)=>(
                  <Box
                    key={`${item.slug}-${index}`}
                    sx={{
                      display:"grid",
                      gridTemplateColumns:{
                        xs:"1fr",
                        sm:"minmax(220px,1fr) 130px auto"
                      },
                      gap:1,
                      alignItems:"center"
                    }}
                  >
                    <FormControl size="small" fullWidth>
                      <InputLabel>Categoría</InputLabel>

                      <Select
                        value={item.slug}
                        label="Categoría"
                        onChange={event=>updateCategory(index,"slug",event.target.value)}
                        disabled={Boolean(selectedPlayer)&&!registrationOpen&&!detailPermissions.categories}
                        MenuProps={{disableScrollLock:true}}
                      >
                        {filteredCategoryOptions(index).map(category=>(
                          <MenuItem key={category.slug} value={category.slug}>
                            {category.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <TextField
                      size="small"
                      type="number"
                      label="Dorsal"
                      value={item.jersey_number}
                      onChange={event=>updateCategory(index,"jersey_number",event.target.value)}
                      disabled={Boolean(selectedPlayer)&&!registrationOpen&&!detailPermissions.categories}
                      inputProps={{min:1,step:1}}
                      fullWidth
                    />

                    <Button
                      size="small"
                      variant="text"
                      onClick={()=>removeCategory(index)}
                      disabled={Boolean(selectedPlayer)&&!registrationOpen&&!detailPermissions.categories}
                      sx={{
                        minWidth:"auto",
                        color:colors.error,
                        textTransform:"none"
                      }}
                    >
                      Quitar
                    </Button>
                  </Box>
                ))}
              </Box>
            )}
          </Box>

          {selectedPlayer&&!registrationOpen&&(
            <Box sx={{mt:2,display:"flex",flexWrap:"wrap",gap:1}}>
              {!detailPermissions.update&&(
                <Button
                  size="small"
                  variant="outlined"
                  onClick={()=>{
                    setFormOpen(false);
                    openRequest(selectedPlayer,"UPDATE");
                  }}
                  sx={{
                    borderColor:colors.buttonOutlinedBorder,
                    color:colors.buttonOutlinedText,
                    textTransform:"none"
                  }}
                >
                  Solicitar modificación de datos
                </Button>
              )}

              {!detailPermissions.categories&&(
                <Button
                  size="small"
                  variant="outlined"
                  onClick={()=>{
                    setFormOpen(false);
                    openRequest(selectedPlayer,"CATEGORIES");
                  }}
                  sx={{
                    borderColor:colors.buttonOutlinedBorder,
                    color:colors.buttonOutlinedText,
                    textTransform:"none"
                  }}
                >
                  Solicitar cambio de categorías
                </Button>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{px:3,py:2,borderTop:`1px solid ${colors.border}`,gap:1}}>
          <Button
            size="small"
            onClick={closeForm}
            disabled={saving}
            sx={{color:colors.buttonText,textTransform:"none"}}
          >
            Cancelar
          </Button>

          {(!selectedPlayer||registrationOpen||detailPermissions.update||detailPermissions.categories)&&(
            <Button
              size="small"
              variant="contained"
              onClick={handleSave}
              disabled={saving}
              sx={{
                minWidth:100,
                backgroundColor:colors.secondary,
                color:colors.buttonPrimaryText,
                boxShadow:"none",
                textTransform:"none",
                "&:hover":{
                  backgroundColor:colors.secondaryHover,
                  boxShadow:"none"
                }
              }}
            >
              {saving
                ?<CircularProgress size={18} sx={{color:colors.buttonPrimaryText}} />
                :selectedPlayer
                  ?"Guardar cambios"
                  :"Registrar jugador"}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={requestOpen}
        onClose={closeRequest}
        fullWidth
        maxWidth="sm"
        disableScrollLock
        PaperProps={{
          sx:{
            borderRadius:shape.radiusLarge,
            boxShadow:"none",
            border:`1px solid ${colors.border}`,
            backgroundColor:colors.surface
          }
        }}
      >
        <DialogTitle>
          <Typography sx={{fontSize:typography.sizes.xl,fontWeight:700,color:colors.text}}>
            Solicitar autorización
          </Typography>
        </DialogTitle>

        <DialogContent>
          <Typography sx={{mb:1.5,fontSize:typography.sizes.sm,color:colors.textSecondary}}>
            {requestType==="CATEGORIES"
              ?`Solicita autorización para modificar las categorías o dorsales de ${requestPlayer?.name||"este jugador"}.`
              :`Solicita autorización para modificar los datos de ${requestPlayer?.name||"este jugador"}.`}
          </Typography>

          <TextField
            size="small"
            label="Motivo de la solicitud"
            value={requestReason}
            onChange={event=>setRequestReason(event.target.value)}
            multiline
            minRows={3}
            fullWidth
          />
        </DialogContent>

        <DialogActions sx={{px:3,py:2,borderTop:`1px solid ${colors.border}`,gap:1}}>
          <Button
            size="small"
            onClick={closeRequest}
            disabled={requesting}
            sx={{color:colors.buttonText,textTransform:"none"}}
          >
            Cancelar
          </Button>

          <Button
            size="small"
            variant="contained"
            onClick={handleRequest}
            disabled={requesting}
            sx={{
              backgroundColor:colors.secondary,
              color:colors.buttonPrimaryText,
              boxShadow:"none",
              textTransform:"none",
              "&:hover":{
                backgroundColor:colors.secondaryHover,
                boxShadow:"none"
              }
            }}
          >
            {requesting
              ?<CircularProgress size={18} sx={{color:colors.buttonPrimaryText}} />
              :"Enviar solicitud"}
          </Button>
        </DialogActions>
      </Dialog>

      <PhotoCropper
        open={cropOpen}
        file={cropSource}
        aspect={cropType==="document"?16/10:3/4}
        title={cropType==="document"?"Ajustar cédula":"Ajustar fotografía"}
        description={
          cropType==="document"
            ?"Mueve la imagen para que el documento completo quede correctamente encuadrado."
            :"Mueve la imagen para encuadrar correctamente el rostro y la parte superior del jugador."
        }
        confirmText={cropType==="document"?"Usar cédula":"Usar fotografía"}
        onClose={()=>{
          setCropOpen(false);
          setCropSource(null);
        }}
        onConfirm={confirmCrop}
      />

      <Carnets
        open={carnetOpen}
        player={carnetPlayer}
        onClose={()=>{
          setCarnetOpen(false);
          setCarnetPlayer(null);
        }}
      />
    </Box>
  );
}